import { type FC, useState } from 'react';
import { AlertTriangle, Building2, Check, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { cn } from '../../../utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../Dropdown/Dropdown';
import { Popover, PopoverContent, PopoverTrigger } from '../../atoms/Popover/Popover';
import { Button } from '../../atoms/Button/Button';
import { Heading } from '../../atoms/Typography/Typography';
import { Text } from '../../atoms/Typography/Typography';

export interface OrgSwitcherOrganization {
  id: string;
  name: string;
  domain?: string;
}

export interface OrgSwitcherLabels {
  selectOrg?: string;
  title?: string;
  description?: string;
  switchingTo?: string;
  tooltip?: string;
  retry?: string;
}

const defaultLabels: Required<OrgSwitcherLabels> = {
  selectOrg: 'Select Organization',
  title: 'Organizations',
  description: 'Switch between your organizations',
  switchingTo: 'Switching...',
  tooltip: 'Switch organization',
  retry: 'Retry',
};

export interface OrgSwitcherProps {
  organizations: OrgSwitcherOrganization[];
  currentOrgId?: string;
  onSwitch?: (orgId: string) => void | Promise<void>;
  variant?: 'header' | 'sidebar';
  collapsed?: boolean;
  isLoading?: boolean;
  error?: string;
  onRetry?: () => void;
  labels?: OrgSwitcherLabels;
  className?: string;
}

export const OrgSwitcher: FC<OrgSwitcherProps> = ({
  organizations,
  currentOrgId,
  onSwitch,
  variant = 'header',
  collapsed = false,
  isLoading = false,
  error,
  onRetry,
  labels: labelsProp,
  className,
}) => {
  const [isSwitching, setIsSwitching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const labels = { ...defaultLabels, ...labelsProp };
  const currentOrg = organizations.find((org) => org.id === currentOrgId);
  const busy = isLoading || isSwitching;

  // Don't show switcher if user only has one org (and no error to display)
  if (!isLoading && !error && organizations.length <= 1) {
    return null;
  }

  const handleSwitch = async (orgId: string) => {
    if (orgId === currentOrgId || !onSwitch) return;

    try {
      setIsSwitching(true);
      await onSwitch(orgId);
    } finally {
      setIsSwitching(false);
    }
  };

  // Sidebar variant - inline expandable list (or side popover when collapsed)
  if (variant === 'sidebar') {
    // Collapsed: side popover so users can switch orgs from a minimized sidebar.
    if (collapsed) {
      return (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="org-switcher"
              title={labels.tooltip}
              aria-label={labels.tooltip}
              disabled={busy}
              className={cn(
                'w-full flex items-center justify-center rounded-md px-0 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer disabled:opacity-50',
                className
              )}
            >
              {busy ? (
                <Loader2 className="h-5 w-5 text-slate-600 dark:text-slate-400 flex-shrink-0 animate-spin" />
              ) : (
                <div className="relative flex-shrink-0">
                  <Building2 className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                  {error && (
                    <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-amber-500" />
                  )}
                </div>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="right"
            align="start"
            sideOffset={8}
            className="w-64 p-1.5"
          >
            <div className="px-2 py-1 mb-1 border-b border-slate-200 dark:border-slate-700/50">
              <Text size="xs" weight="semibold" className="text-slate-500 dark:text-slate-400">
                {labels.title}
              </Text>
            </div>
            {error && (
              <div className="mx-1 my-1 flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400" role="alert">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="flex-1 truncate">{error}</span>
                {onRetry && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onRetry(); }}
                    className="text-xs underline hover:no-underline flex-shrink-0"
                    aria-label={labels.retry}
                  >
                    {labels.retry}
                  </button>
                )}
              </div>
            )}
            {organizations.map((org) => (
              <button
                type="button"
                key={org.id}
                onClick={() => handleSwitch(org.id)}
                disabled={busy}
                className={cn(
                  'w-full flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors text-left disabled:opacity-50',
                  org.id === currentOrgId
                    ? 'bg-slate-100 text-slate-900 dark:bg-slate-700/50 dark:text-slate-100'
                    : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/30'
                )}
              >
                <span className="flex-1 truncate">{org.name}</span>
                {org.id === currentOrgId && (
                  <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                )}
              </button>
            ))}
          </PopoverContent>
        </Popover>
      );
    }

    // Expanded: inline expandable list.
    return (
      <div className={cn('space-y-1', className)}>
        <div
          className="flex items-center gap-2.5 rounded-md px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
          data-testid="org-switcher"
        >
          {busy ? (
            <Loader2 className="h-5 w-5 text-slate-600 dark:text-slate-400 flex-shrink-0 animate-spin" />
          ) : (
            <div className="relative flex-shrink-0">
              <Building2 className="h-5 w-5 text-slate-600 dark:text-slate-400" />
              {error && (
                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-amber-500" />
              )}
            </div>
          )}
          <Text
            as="span"
            className="flex-1 text-slate-700 dark:text-slate-300 truncate"
          >
            {currentOrg?.name || labels.selectOrg}
          </Text>
          {isOpen ? (
            <ChevronUp className="h-4 w-4 text-slate-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-500" />
          )}
        </div>

        {/* Error state */}
        {error && (
          <div className="mx-3 mt-1" role="alert">
            <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="flex-1 truncate">{error}</span>
              {onRetry && (
                <button
                  onClick={(e) => { e.stopPropagation(); onRetry(); }}
                  className="text-xs underline hover:no-underline flex-shrink-0"
                  aria-label={labels.retry}
                >
                  {labels.retry}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Expanded organization list */}
        {isOpen && (
          <div className="ml-7 mb-2 space-y-1">
            {organizations.map((org) => (
              <Button
                key={org.id}
                variant="ghost"
                size="sm"
                onClick={() => handleSwitch(org.id)}
                disabled={busy}
                className={cn(
                  'w-full flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors justify-start h-auto',
                  org.id === currentOrgId
                    ? 'bg-slate-100 text-slate-900 dark:bg-slate-700/50 dark:text-slate-100'
                    : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700/30'
                )}
              >
                <span className="flex-1 text-left truncate">{org.name}</span>
                {org.id === currentOrgId && (
                  <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                )}
              </Button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Header variant - dropdown menu (default)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn('h-10 px-3 flex items-center gap-2', className)}
          disabled={busy}
          title={labels.tooltip}
          data-testid="org-switcher"
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Building2 className="h-4 w-4" />
          )}
          <Text as="span" className="max-sm:hidden max-w-[150px] truncate">
            {currentOrg?.name || labels.selectOrg}
          </Text>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64 p-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/50 rounded-lg shadow-lg"
      >
        <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700/50">
          <Heading level="h6" as="h3">
            {labels.title}
          </Heading>
          <Text size="xs" color="muted" className="dark:text-slate-400">
            {labels.description}
          </Text>
        </div>

        <div className="py-1 max-h-[300px] overflow-y-auto">
          {organizations.map((org) => (
            <DropdownMenuItem
              key={org.id}
              onClick={() => handleSwitch(org.id)}
              disabled={busy}
              className="flex items-center gap-3 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/30 rounded-md transition-colors mx-1 cursor-pointer"
            >
              <div className="w-8 h-8 rounded-md bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                <Building2 className="h-4 w-4 text-slate-500 dark:text-slate-400" />
              </div>
              <div className="flex-1 min-w-0">
                <Text size="sm" weight="medium" className="truncate">
                  {org.name}
                </Text>
                {org.domain && (
                  <Text
                    size="xs"
                    color="muted"
                    className="truncate dark:text-slate-400"
                  >
                    {org.domain}
                  </Text>
                )}
              </div>
              {org.id === currentOrgId && (
                <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
              )}
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
OrgSwitcher.displayName = 'OrgSwitcher';
